// Settings are stored in appconfig.json

import { 
    ArtifactIdentifier, 
    ProductArtifactsTypes, 
    ProductsApi,
    splitArtifactId 
} from "shared-components";
import * as _ from "lodash"

// Using 'yes'/'no' because configuration contains only strings
// and 'true'/'false' might be confused as boolean values.
type YesOrNo = "yes" | "no";

export interface IFeatureFlags {
    // 1. [RepoPanel] Debug panel that gives buttons to reset state that are not otherwise available through UI
    // 2. (no other effects yet; flag may be used for any additional debugging tools / info)
    DebugMode: YesOrNo;
    // [ProductPage] Show build pipelines mapped manually by users or the auto derived ones
    AutoAssignedBuildDefinitionsEnabled: YesOrNo;
    // [ProductPage] Show auto-assigned release pipelines for the product 
    AutoAssignedReleaseDefinitionsEnabled: YesOrNo;
    // Show repo assignment configuration settings.
    AutoAssignmentManagementEnabled: YesOrNo;
    // Show debugging elements for auto-assignment UX.
    AutoAssignmentDebugModeEnabled: YesOrNo;
    // [ProductPage] Show UI for manual mapping of release pipelines
    ManuallyAssignedReleaseDefinitionsEnabled: YesOrNo;
}

export interface IBlockedOrgSettings {
    orgId: string;
    blockedArtifacts: string[];
}

export class Config {
    // Map for memoziation of isMonoRepo flag.
    // Without this, we would make calls to Product Catalog for each assigned repo on every re-render.
    static isMonoRepoMap = new Map<string, boolean>();

    static getValue<T = string>(key: string): T {
        const cfg = global["config"] || {};
        return cfg[key];
    }

    static get featureFlags() {
        return this.getValue<IFeatureFlags>("UI:FEATURE_FLAGS");
    }

    static get isDebugMode() {
        return this.featureFlags && this.featureFlags.DebugMode?.toLowerCase() === "yes";
    }

    static get isAutoAssignedBuildDefinitionsEnabled() {
        return this.featureFlags && this.featureFlags.AutoAssignedBuildDefinitionsEnabled?.toLowerCase() == "yes";
    }

    static get isAutoAssignedReleaseDefinitionsEnabled() {
        return this.featureFlags && this.featureFlags.AutoAssignedReleaseDefinitionsEnabled?.toLowerCase() === "yes";
    }

    static get isManuallyAssignedReleaseDefinitionsEnabled() {
        return this.featureFlags && this.featureFlags.ManuallyAssignedReleaseDefinitionsEnabled?.toLowerCase() === "yes";
    }

    static get isAutoAssignmentDebugModeEnabled() {
        return this.featureFlags && this.featureFlags.AutoAssignmentDebugModeEnabled?.toLowerCase() === "yes";
    }

    static get isDevEnvironment() {
        return this.getValue<string>("UI:ENVIRONMENT")?.toLowerCase() === "development";
    }
    
    static IsProductAllowedForCypressTesting(productId: string): boolean {
        return _.find(this.getValue<string[]>("UI:ALLOWED_PRODUCTS_FOR_CYPRESS_TESTING"), productId) !== null
    }

    static IsUserAllowedForCypressTesting(userLocalAccountId: string): boolean {
        return _.find(this.getValue<string[]>("UI:ALLOWED_USERS_FOR_CYPRESS_TESTING"), userLocalAccountId) !== null
    }

    static isArtifactMonoRepo = async (artifact: ArtifactIdentifier) => {
        let isMonoRepo = Config.isMonoRepoMap.get(artifact.artifactId);
        if (isMonoRepo != undefined){
            return isMonoRepo;
        }
        const productsWhereRepoIsAssigned = await ProductsApi.getProductsFromArtifact(artifact);
        // Mono Repo is a repo artifact that is assigned to multiple products
        isMonoRepo = productsWhereRepoIsAssigned.length > 1;
        Config.isMonoRepoMap.set(artifact.artifactId, isMonoRepo);
        return isMonoRepo;
    }

    // Specific ado accounts are excluded from auto assignment, details here: https://dev.azure.com/mseng/1ES/_workitems/edit/2009687
    static isExcludedAdoOrgForAutoAssignment(artifact: ArtifactIdentifier): boolean {
        const orgId = splitArtifactId(artifact.artifactId).organizationId;
        return this.getValue("UI:AUTO_ASSIGNMENT_EXCLUDED_ADO_ORG_IDS").includes(orgId);
    }

    static async isAutoAssignmentManagementEnabled(productId: string, artifact: ArtifactIdentifier): Promise<boolean> {
        if (artifact.artifactType != ProductArtifactsTypes.Git) {
            return false;
        }
        const isMonoRepo = await this.isArtifactMonoRepo(artifact);
        return (
            this.featureFlags && 
            this.featureFlags.AutoAssignmentManagementEnabled?.toLowerCase() === "yes" && 
            this.getValue("UI:AUTO_ASSIGNMENT_MANAGEMENT_ENABLED_IDS").includes(productId) && 
            !this.isExcludedAdoOrgForAutoAssignment(artifact) &&
            isMonoRepo
        );
    }
}
